import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "project-stakeholders" }
const _hoisted_2 = { class: "stakeholders-header" }
const _hoisted_3 = { class: "stakeholders-title" }
const _hoisted_4 = { class: "stakeholders-actions" }
const _hoisted_5 = {
  key: 0,
  class: "stakeholders-table"
}
const _hoisted_6 = { class: "stakeholders-list-header" }
const _hoisted_7 = { class: "header" }
const _hoisted_8 = { class: "avatar" }
const _hoisted_9 = { class: "fullname" }
const _hoisted_10 = { class: "role" }
const _hoisted_11 = {
  key: 1,
  class: "empty"
}
const _hoisted_12 = { class: "add-stakeholder-form" }
const _hoisted_13 = {
  key: 0,
  class: "loading apollo"
}
const _hoisted_14 = { class: "edit-stakeholder-form" }
const _hoisted_15 = { class: "readonly-field" }
const _hoisted_16 = { class: "readonly-field-label" }
const _hoisted_17 = { class: "readonly-field-value" }
const _hoisted_18 = {
  key: 0,
  class: "loading apollo"
}

import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";

import ButtonIcon from "@/components/ButtonIcon.vue";
import EntitiesList from "@/components/list/EntitiesList.vue";
import Modal from "@/components/Modal.vue";
import FormItem from "@/components/form/FormItem.vue";
import BasicButton from "@/components/BasicButton.vue";

import { GET_PROJECT_STAKEHOLDERS } from "@/graphql/project/queries";
import {
  ADD_PROJECT_STAKEHOLDER,
  UPDATE_PROJECT_STAKEHOLDER,
  DELETE_PROJECT_STAKEHOLDER,
} from "@/graphql/project/mutations";
import { GET_DETAILED_EMPLOYEES } from "@/graphql/user/queries";

import { useMutation } from "@vue/apollo-composable";

import enUK from "@/locales/project/en-UK.json";

interface StakeholderUser {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
}

interface StakeholderData {
  user: StakeholderUser;
  role: string;
}

interface StakeholderRow {
  id: string; // Composite key: userId-role
  avatar: {
    name: string;
    avatarUrl?: string;
  };
  fullname: string;
  role: string;
  raw: StakeholderData;
}

interface EmployeeOption {
  id: string;
  name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectStakeholders',
  props: {
  projectId: {
    type: String,
    required: true,
  },
},
  emits: ["stakeholdersUpdated"],
  setup(__props, { emit: __emit }) {

const STAKEHOLDER_ROLES = [
  "sponsor",
  "product_owner",
  "delivery_manager",
  "account_manager",
] as const;

const ROLE_LABELS: Record<string, string> = {
  sponsor: "Sponsor",
  product_owner: "Product Owner",
  delivery_manager: "Delivery Manager",
  account_manager: "Account Manager",
};

const props = __props;

const emit = __emit;

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

provide("columnsOrder", {
  avatar: null,
  fullname: null,
  role: null,
});

const { result, onError, refetch } = useQuery(GET_PROJECT_STAKEHOLDERS, () => ({
  id: props.projectId,
}));

const rawStakeholders = computed(() => {
  return result.value?.project?.stakeholders ?? [];
});

const stakeholders = computed<StakeholderRow[]>(() => {
  return rawStakeholders.value.map((stakeholder: StakeholderData) => {
    const fullName = [stakeholder.user.firstName, stakeholder.user.lastName]
      .filter(Boolean)
      .join(" ");

    return {
      id: `${stakeholder.user.id}-${stakeholder.role}`,
      avatar: {
        name: fullName,
        avatarUrl: stakeholder.user.avatarUrl,
      },
      fullname: fullName,
      role: ROLE_LABELS[stakeholder.role] || stakeholder.role,
      raw: stakeholder,
    };
  });
});

const isAddModalVisible = ref(false);
const isEditModalVisible = ref(false);
const selectedStakeholder = ref<StakeholderData | null>(null);

// Add modal state
const selectedEmployeeId = ref("");
const selectedRole = ref("");

// Edit modal state
const editSelectedRole = ref("");

const {
  result: employeesResult,
  loading: loadingEmployees,
  onError: onEmployeesError,
} = useQuery(GET_DETAILED_EMPLOYEES);

onEmployeesError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getEmployees"));
});

const employees = computed(() => {
  return employeesResult.value?.users ?? [];
});

// Get clients from the project stakeholders query
const projectClients = computed(() => {
  return result.value?.project?.clients ?? [];
});

const userOptions = computed<EmployeeOption[]>(() => {
  // Combine employees and project clients, removing duplicates
  const employeeMap = new Map<string, EmployeeOption>();

  // Add employees
  employees.value.forEach((employee: User) => {
    employeeMap.set(employee.id, {
      id: employee.id,
      name: [employee.firstName, employee.lastName].filter(Boolean).join(" "),
    });
  });

  // Add project clients (they may not be in the employees list)
  projectClients.value.forEach((client: { user: StakeholderUser }) => {
    if (!employeeMap.has(client.user.id)) {
      employeeMap.set(client.user.id, {
        id: client.user.id,
        name: [client.user.firstName, client.user.lastName]
          .filter(Boolean)
          .join(" "),
      });
    }
  });

  return Array.from(employeeMap.values());
});

const roleOptions = computed(() => {
  return STAKEHOLDER_ROLES.reduce(
    (acc, role) => {
      acc[role] = ROLE_LABELS[role];
      return acc;
    },
    {} as Record<string, string>,
  );
});

const {
  mutate: addProjectStakeholder,
  loading: addingStakeholder,
  onError: onAddStakeholderError,
  onDone: onAddStakeholderDone,
} = useMutation(ADD_PROJECT_STAKEHOLDER, {
  refetchQueries: [
    {
      query: GET_PROJECT_STAKEHOLDERS,
      variables: {
        id: props.projectId,
      },
    },
  ],
});

onAddStakeholderError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.addStakeholder"));
});

onAddStakeholderDone((result) => {
  if (result.data?.addProjectStakeholder?.status === "ok") {
    toast.success(t("success.addStakeholder"));
    closeAddModal();
    refetch();
    emit("stakeholdersUpdated");
  } else {
    toast.error(
      result.data?.addProjectStakeholder?.error || t("errors.addStakeholder"),
    );
  }
});

const {
  mutate: updateProjectStakeholder,
  loading: updatingStakeholder,
  onError: onUpdateStakeholderError,
  onDone: onUpdateStakeholderDone,
} = useMutation(UPDATE_PROJECT_STAKEHOLDER, {
  refetchQueries: [
    {
      query: GET_PROJECT_STAKEHOLDERS,
      variables: {
        id: props.projectId,
      },
    },
  ],
});

onUpdateStakeholderError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateStakeholder"));
});

onUpdateStakeholderDone((result) => {
  if (result.data?.updateProjectStakeholder?.status === "ok") {
    toast.success(t("success.updateStakeholder"));
    closeEditModal();
    refetch();
    emit("stakeholdersUpdated");
  } else {
    toast.error(
      result.data?.updateProjectStakeholder?.error ||
        t("errors.updateStakeholder"),
    );
  }
});

const {
  mutate: deleteProjectStakeholder,
  loading: deletingStakeholder,
  onError: onDeleteStakeholderError,
  onDone: onDeleteStakeholderDone,
} = useMutation(DELETE_PROJECT_STAKEHOLDER, {
  refetchQueries: [
    {
      query: GET_PROJECT_STAKEHOLDERS,
      variables: {
        id: props.projectId,
      },
    },
  ],
});

onDeleteStakeholderError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteStakeholder"));
});

onDeleteStakeholderDone((result) => {
  if (result.data?.deleteProjectStakeholder?.status === "ok") {
    toast.success(t("success.deleteStakeholder"));
    refetch();
    emit("stakeholdersUpdated");
  } else {
    toast.error(
      result.data?.deleteProjectStakeholder?.error ||
        t("errors.deleteStakeholder"),
    );
  }
});

const isLoading = computed(() => {
  return (
    loadingEmployees.value ||
    addingStakeholder.value ||
    updatingStakeholder.value ||
    deletingStakeholder.value
  );
});

function formatAvatar(value: { name: string; avatarUrl?: string }): {
  name: string;
  avatarUrl?: string;
} {
  return value;
}

const formatters = {
  avatar: formatAvatar,
};

function showAddModal(): void {
  selectedEmployeeId.value = "";
  selectedRole.value = "";
  isAddModalVisible.value = true;
}

function closeAddModal(): void {
  isAddModalVisible.value = false;
  selectedEmployeeId.value = "";
  selectedRole.value = "";
}

async function handleAddStakeholder(): Promise<void> {
  if (!selectedEmployeeId.value || !selectedRole.value) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  await addProjectStakeholder({
    projectId: props.projectId,
    userId: selectedEmployeeId.value,
    role: selectedRole.value,
  });
}

function handleStakeholderClick(stakeholder: StakeholderRow): void {
  selectedStakeholder.value = stakeholder.raw;
  editSelectedRole.value = stakeholder.raw.role;
  isEditModalVisible.value = true;
}

function closeEditModal(): void {
  isEditModalVisible.value = false;
  selectedStakeholder.value = null;
  editSelectedRole.value = "";
}

async function handleUpdateStakeholder(): Promise<void> {
  if (!selectedStakeholder.value || !editSelectedRole.value) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  await updateProjectStakeholder({
    projectId: props.projectId,
    userId: selectedStakeholder.value.user.id,
    oldRole: selectedStakeholder.value.role,
    newRole: editSelectedRole.value,
  });
}

async function handleDeleteStakeholder(): Promise<void> {
  if (!selectedStakeholder.value) {
    return;
  }

  await deleteProjectStakeholder({
    projectId: props.projectId,
    userId: selectedStakeholder.value.user.id,
    role: selectedStakeholder.value.role,
  });
}

onError((error) => {
  logErrorMessages(error);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(_unref(t)("labels.stakeholdersAndResponsibilities")), 1),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(ButtonIcon, {
          label: _unref(t)('buttons.addKeyResponsibility'),
          "icon-name": "material-symbols:add",
          onClick: showAddModal
        }, null, 8, ["label"])
      ])
    ]),
    (stakeholders.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("ul", _hoisted_6, [
            _createElementVNode("li", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("labels.avatar")), 1),
              _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(t)("labels.fullname")), 1),
              _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)("labels.role")), 1)
            ])
          ]),
          _createVNode(EntitiesList, {
            elements: stakeholders.value,
            formatters: formatters,
            "avatar-column": "avatar",
            onGoEntityDetails: handleStakeholderClick
          }, null, 8, ["elements"])
        ]))
      : (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_unref(t)("empty.stakeholders")), 1)),
    (isAddModalVisible.value)
      ? (_openBlock(), _createBlock(Modal, {
          key: 2,
          onClose: closeAddModal
        }, {
          header: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("titles.addKeyResponsibility")), 1)
          ]),
          body: _withCtx(() => [
            _createElementVNode("div", _hoisted_12, [
              _createVNode(FormItem, {
                input: selectedEmployeeId.value,
                "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((selectedEmployeeId).value = $event)),
                label: _unref(t)('labels.employee'),
                name: "employee",
                "input-type": "select",
                options: userOptions.value
              }, null, 8, ["input", "label", "options"]),
              _createVNode(FormItem, {
                input: selectedRole.value,
                "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((selectedRole).value = $event)),
                label: _unref(t)('labels.role'),
                name: "role",
                "input-type": "select",
                options: roleOptions.value
              }, null, 8, ["input", "label", "options"])
            ]),
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(_unref(t)("loading")), 1))
              : _createCommentVNode("", true)
          ]),
          footer: _withCtx(() => [
            _createVNode(BasicButton, {
              class: "add-button",
              type: "submit",
              disabled: isLoading.value || !selectedEmployeeId.value || !selectedRole.value,
              onClick: handleAddStakeholder
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.addKeyResponsibility")), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(BasicButton, {
              class: "cancel-button",
              onClick: closeAddModal
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (isEditModalVisible.value && selectedStakeholder.value)
      ? (_openBlock(), _createBlock(Modal, {
          key: 3,
          onClose: closeEditModal
        }, {
          header: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("titles.editKeyResponsibility")), 1)
          ]),
          body: _withCtx(() => [
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(t)("labels.employee")), 1),
                _createElementVNode("div", _hoisted_17, _toDisplayString([
                  selectedStakeholder.value.user.firstName,
                  selectedStakeholder.value.user.lastName,
                ]
                  .filter(Boolean)
                  .join(" ")), 1)
              ]),
              _createVNode(FormItem, {
                input: editSelectedRole.value,
                "onUpdate:input": _cache[2] || (_cache[2] = ($event: any) => ((editSelectedRole).value = $event)),
                label: _unref(t)('labels.role'),
                name: "role",
                "input-type": "select",
                options: roleOptions.value
              }, null, 8, ["input", "label", "options"])
            ]),
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_18, _toDisplayString(_unref(t)("loading")), 1))
              : _createCommentVNode("", true)
          ]),
          footer: _withCtx(() => [
            _createVNode(BasicButton, {
              class: "update-button",
              type: "submit",
              disabled: isLoading.value || !editSelectedRole.value,
              onClick: handleUpdateStakeholder
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.save")), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(BasicButton, {
              class: "delete-button",
              disabled: isLoading.value,
              onClick: handleDeleteStakeholder
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.delete")), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(BasicButton, {
              class: "cancel-button",
              onClick: closeEditModal
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})